import { Material } from "@animech/axiom-format/src/definitions/definition-bindings";
import { skuKey, skuTable } from "./sku-lookup";
import { ArticleNumbersColorAndQuantity } from "src/api/api-data-manager";
import {
	ChairSlot,
	SeatingType,
	SIDE_COUNT,
	TableType,
} from "src/definitions/type-definitions";

export type ArticleInput = {
	tableType: TableType;
	tableMaterialWood: Material | undefined;
	tableMaterialMetal: Material;
	chairs: ChairSlot[];
};

function materialColor(wood: Material | undefined, metal: Material): string {
	if (wood === undefined) {
		return String(metal);
	}
	return `${wood} / ${metal}`;
}

export function generateArticleNumbersAndQuantities(
	input: ArticleInput,
): ArticleNumbersColorAndQuantity[] {
	const result: ArticleNumbersColorAndQuantity[] = [];

	// 1. Table line item
	const tableKey = skuKey(
		input.tableType,
		input.tableMaterialWood,
		input.tableMaterialMetal,
	);
	const tableSku = skuTable[tableKey];
	if (tableSku) {
		result.push({
			articleNumber: tableSku,
			quantity: 1,
			color: materialColor(input.tableMaterialWood, input.tableMaterialMetal),
		});
	} else {
		console.warn(`Article number not found for table: ${tableKey}`);
	}

	// 2. Seating line items — aggregate by SKU.
	//    Only representative slots (indices 0..SIDE_COUNT-1) carry the
	//    per-side count and materials. They have visible=false (they're
	//    metadata, not rendered objects), so we filter on count > 0 only.
	const seatingAgg = new Map<
		string,
		{ sku: string; color: string; quantity: number }
	>();

	const repCount = Math.min(SIDE_COUNT, input.chairs.length);
	for (let i = 0; i < repCount; i++) {
		const slot = input.chairs[i];
		if (slot.count <= 0) {
			continue;
		}

		const chairKey = skuKey(
			slot.chairType as SeatingType,
			slot.chairMaterialWood,
			slot.chairMaterialMetal,
		);
		const chairSku = skuTable[chairKey];
		if (!chairSku) {
			console.warn(`Article number not found for seating: ${chairKey}`);
			continue;
		}

		const existing = seatingAgg.get(chairSku);
		if (existing) {
			existing.quantity += slot.count;
		} else {
			seatingAgg.set(chairSku, {
				sku: chairSku,
				color: materialColor(slot.chairMaterialWood, slot.chairMaterialMetal),
				quantity: slot.count,
			});
		}
	}

	for (const { sku, color, quantity } of seatingAgg.values()) {
		result.push({ articleNumber: sku, quantity, color });
	}

	return result;
}
